import { Component, OnInit, ViewChild } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';
import {
  BinanceDepositService,
  WalletBalancesQueryDto,
  WalletBalancesResult,
  UserWalletBalance,
} from '../../services/binance-deposit.service';
import {
  BinanceWithdrawalService,
  ManualDebitDto,
  ManualCreditDto,
} from '../../services/binance-withdrawal.service';

@Component({
  selector: 'app-wallets-cardex-conciliator',
  templateUrl: './wallets-cardex-conciliator.component.html',
  styleUrls: ['./wallets-cardex-conciliator.component.sass']
})
export class WalletsCardexConciliatorComponent implements OnInit {
  displayedColumns: string[] = [
    'userName',
    'userId',
    'userEmail',
    'usdtAbonados',
    'usdtDebitados',
    'saldoDisponible',
    'lastMovementDate',
    'actions'
  ];

  dataSource = new MatTableDataSource<UserWalletBalance>([]);

  // Paginación
  total = 0;
  pageSize = 20;
  pageIndex = 0;

  // Estados
  loading = false;
  loadingBalances = false;
  errorMsg = '';
  successMsg = '';
  expandedCardId: number | null = null;

  // Protección contra múltiples peticiones
  isProcessingDebit = false;
  isProcessingCredit = false;

  // Rate limiting - tiempo de espera después de 429
  debitRetryAfterSeconds = 0;
  creditRetryAfterSeconds = 0;
  debitRetryTimer: any = null;
  creditRetryTimer: any = null;

  // Filtros
  filters: WalletBalancesQueryDto = {
    coin: 'USDT',
    limit: 20,
    page: 1
  };

  // Resumen
  summary: any = null;

  // Modal de débito manual
  showManualDebitModal = false;
  selectedUser: UserWalletBalance | null = null;
  manualDebitForm: FormGroup;

  // Modal de crédito manual
  showManualCreditModal = false;
  selectedUserCredit: UserWalletBalance | null = null;
  manualCreditForm: FormGroup;

  // Destroy subject for cleanup
  private destroy$ = new Subject<void>();

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  constructor(
    private depositService: BinanceDepositService,
    private withdrawalService: BinanceWithdrawalService,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {
    // Initialize manual debit form
    this.manualDebitForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01), this.decimalValidator(2)]],
      reason: ['', [Validators.required, Validators.minLength(5)]]
    });

    // Initialize manual credit form
    this.manualCreditForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01), this.decimalValidator(2)]],
      reason: ['', [Validators.required, Validators.minLength(5)]]
    });
  }

  toggleCard(id: number): void {
    this.expandedCardId = this.expandedCardId === id ? null : id;
  }

  ngOnInit() {
    this.loadWalletBalances();
  }

  loadWalletBalances(event?: PageEvent) {
    this.loading = true;
    this.errorMsg = '';

    if (event) {
      this.pageIndex = event.pageIndex;
      this.pageSize = event.pageSize;
      this.filters.page = event.pageIndex + 1;
      this.filters.limit = event.pageSize;
    }

    this.depositService.getWalletBalances(this.filters).subscribe({
      next: (result: WalletBalancesResult) => {
        this.dataSource.data = result.users;
        this.total = result.pagination.total;
        this.summary = result.summary;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.errorMsg = err.message || 'Error al cargar saldos de usuarios';
      }
    });
  }


  applyFilters() {
    this.pageIndex = 0;
    this.filters.page = 1;
    this.loadWalletBalances();
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  formatUSDT(amount: number): string {
    return `${this.formatCurrency(amount)} USDT`;
  }



  exportToExcel() {
    const exportQuery = { ...this.filters };
    delete exportQuery.page;
    delete exportQuery.limit;

    this.depositService.downloadWalletBalancesExcel(exportQuery);
  }

  clearFilters() {
    this.filters = {
      coin: 'USDT',
      limit: 20,
      page: 1
    };
    this.applyFilters();
  }

  formatDate(date: Date | undefined): string {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-CL');
  }

  /**
   * Maneja errores de rate limiting (429) extrayendo información detallada
   */
  private handleRateLimitError(error: any, operationType: 'DEBIT' | 'CREDIT'): string {
    // Verificar si es un error 429
    if (error?.error?.statusCode === 429 || error?.status === 429) {
      const details = error?.error?.details || {};
      const retryAfter = details.retryAfterSeconds || 60;
      const previousOps = details.previousOperations || 'desconocidas';
      const currentRequests = details.currentRequests || 0;
      const maxRequests = details.maxRequests || 2;

      // Configurar timer de reintento
      if (operationType === 'DEBIT') {
        this.debitRetryAfterSeconds = retryAfter;
        this.startDebitRetryTimer();
      } else {
        this.creditRetryAfterSeconds = retryAfter;
        this.startCreditRetryTimer();
      }

      return `Has excedido el límite de operaciones manuales (${currentRequests}/${maxRequests}). ` +
             `Operaciones previas: ${previousOps}. ` +
             `Por favor espera ${retryAfter} segundos antes de intentar nuevamente.`;
    }

    // Error genérico
    return error?.error?.message || error?.message ||
           `Error al ${operationType === 'DEBIT' ? 'quitar' : 'agregar'} el saldo`;
  }

  /**
   * Inicia el temporizador de reintento para débito
   */
  private startDebitRetryTimer(): void {
    if (this.debitRetryTimer) {
      clearInterval(this.debitRetryTimer);
    }

    this.debitRetryTimer = setInterval(() => {
      this.debitRetryAfterSeconds--;
      if (this.debitRetryAfterSeconds <= 0) {
        clearInterval(this.debitRetryTimer);
        this.debitRetryTimer = null;
      }
    }, 1000);
  }

  /**
   * Inicia el temporizador de reintento para crédito
   */
  private startCreditRetryTimer(): void {
    if (this.creditRetryTimer) {
      clearInterval(this.creditRetryTimer);
    }

    this.creditRetryTimer = setInterval(() => {
      this.creditRetryAfterSeconds--;
      if (this.creditRetryAfterSeconds <= 0) {
        clearInterval(this.creditRetryTimer);
        this.creditRetryTimer = null;
      }
    }, 1000);
  }

  /**
   * Validador personalizado para limitar decimales
   */
  decimalValidator(maxDecimals: number) {
    return (control: any) => {
      if (!control.value) return null;

      const value = control.value.toString();
      // Aceptar tanto punto como coma como separador decimal
      const decimalPart = value.includes(',') ? value.split(',')[1] : value.split('.')[1];

      if (decimalPart && decimalPart.length > maxDecimals) {
        return { maxDecimals: { max: maxDecimals, actual: decimalPart.length } };
      }

      return null;
    };
  }

  /**
   * Maneja el input del campo amount para permitir solo comas como separador decimal (débito)
   */
  onAmountInput(event: any): void {
    let value = event.target.value;

    // Reemplazar puntos por comas
    value = value.replace(/\./g, ',');

    // Permitir solo números y una coma
    value = value.replace(/[^0-9,]/g, '');

    // Permitir solo una coma
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }

    // Limitar a 2 decimales después de la coma
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].substring(0, 2);
    }

    event.target.value = value;

    // Actualizar el valor del formulario (convertir coma a punto para validación)
    const numericValue = value.replace(',', '.');
    this.manualDebitForm.patchValue({ amount: numericValue }, { emitEvent: false });
  }

  /**
   * Maneja el input del campo amount para permitir solo comas como separador decimal (crédito)
   */
  onAmountInputCredit(event: any): void {
    let value = event.target.value;

    // Reemplazar puntos por comas
    value = value.replace(/\./g, ',');

    // Permitir solo números y una coma
    value = value.replace(/[^0-9,]/g, '');

    // Permitir solo una coma
    const parts = value.split(',');
    if (parts.length > 2) {
      value = parts[0] + ',' + parts.slice(1).join('');
    }

    // Limitar a 2 decimales después de la coma
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + ',' + parts[1].substring(0, 2);
    }

    event.target.value = value;

    // Actualizar el valor del formulario (convertir coma a punto para validación)
    const numericValue = value.replace(',', '.');
    this.manualCreditForm.patchValue({ amount: numericValue }, { emitEvent: false });
  }

  /**
   * Abre el modal de débito manual
   */
  openManualDebitModal(user: UserWalletBalance, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedUser = user;
    this.manualDebitForm.reset();
    this.errorMsg = '';
    this.successMsg = '';
    this.showManualDebitModal = true;
  }

  /**
   * Cierra el modal de débito manual
   */
  closeManualDebitModal(): void {
    this.showManualDebitModal = false;
    this.selectedUser = null;
    this.manualDebitForm.reset();
    this.errorMsg = '';
  }

  /**
   * Ejecuta el débito manual
   */
  executeManualDebit(): void {
    // Prevenir múltiples peticiones simultáneas
    if (this.isProcessingDebit) {
      return;
    }

    // Verificar si hay un tiempo de espera activo por rate limiting
    if (this.debitRetryAfterSeconds > 0) {
      this.errorMsg = `Debes esperar ${this.debitRetryAfterSeconds} segundos antes de realizar otra operación de débito`;
      return;
    }

    if (!this.selectedUser || this.manualDebitForm.invalid) {
      return;
    }

    const amountValue = this.manualDebitForm.get('amount')?.value;
    const reason = this.manualDebitForm.get('reason')?.value;

    // Convertir a número (puede venir como string del formulario)
    const amount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;

    // Validar que haya saldo suficiente
    if (amount > this.selectedUser.saldoDisponible) {
      this.errorMsg = 'El monto a debitar excede el saldo disponible del usuario';
      return;
    }

    this.errorMsg = '';
    this.successMsg = '';

    // Bloquear nuevas peticiones
    this.isProcessingDebit = true;

    const dto: ManualDebitDto = {
      userId: this.selectedUser.userId,
      amount: amount,
      reason: reason.trim()
    };


    this.withdrawalService
      .manualDebit(this.selectedUser.userId, dto)
      .pipe(
        takeUntil(this.destroy$),
        // Asegurar que siempre se desbloquee, incluso si hay error
        finalize(() => {
          this.isProcessingDebit = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.successMsg = 'Saldo quitado exitosamente';
          this.closeManualDebitModal();
          this.loadWalletBalances();

          // Clear success message after 5 seconds
          setTimeout(() => {
            this.successMsg = '';
          }, 5000);
        },
        error: (error) => {
          // Manejar tanto errores de tipo string (del interceptor) como HttpErrorResponse (429)
          if (typeof error === 'string') {
            this.errorMsg = error;
          } else {
            this.errorMsg = this.handleRateLimitError(error, 'DEBIT');
          }
        }
      });
  }

  /**
   * Abre el modal de crédito manual
   */
  openManualCreditModal(user: UserWalletBalance, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.selectedUserCredit = user;
    this.manualCreditForm.reset();
    this.errorMsg = '';
    this.successMsg = '';
    this.showManualCreditModal = true;
  }

  /**
   * Cierra el modal de crédito manual
   */
  closeManualCreditModal(): void {
    this.showManualCreditModal = false;
    this.selectedUserCredit = null;
    this.manualCreditForm.reset();
    this.errorMsg = '';
  }

  /**
   * Ejecuta el crédito manual
   */
  executeManualCredit(): void {
    // Prevenir múltiples peticiones simultáneas
    if (this.isProcessingCredit) {
      return;
    }

    // Verificar si hay un tiempo de espera activo por rate limiting
    if (this.creditRetryAfterSeconds > 0) {
      this.errorMsg = `Debes esperar ${this.creditRetryAfterSeconds} segundos antes de realizar otra operación de crédito`;
      return;
    }

    if (!this.selectedUserCredit || this.manualCreditForm.invalid) {
      return;
    }

    const amountValue = this.manualCreditForm.get('amount')?.value;
    const reason = this.manualCreditForm.get('reason')?.value;

    // Convertir a número (puede venir como string del formulario)
    const amount = typeof amountValue === 'string' ? parseFloat(amountValue) : amountValue;

    this.errorMsg = '';
    this.successMsg = '';

    // Bloquear nuevas peticiones
    this.isProcessingCredit = true;

    const dto: ManualCreditDto = {
      userId: this.selectedUserCredit.userId,
      amount: amount,
      reason: reason.trim()
    };


    this.withdrawalService
      .manualCredit(this.selectedUserCredit.userId, dto)
      .pipe(
        takeUntil(this.destroy$),
        // Asegurar que siempre se desbloquee, incluso si hay error
        finalize(() => {
          this.isProcessingCredit = false;
        })
      )
      .subscribe({
        next: (response) => {
          this.successMsg = 'Saldo agregado exitosamente';
          this.closeManualCreditModal();
          this.loadWalletBalances();

          // Clear success message after 5 seconds
          setTimeout(() => {
            this.successMsg = '';
          }, 5000);
        },
        error: (error) => {
          // Manejar tanto errores de tipo string (del interceptor) como HttpErrorResponse (429)
          if (typeof error === 'string') {
            this.errorMsg = error;
          } else {
            this.errorMsg = this.handleRateLimitError(error, 'CREDIT');
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Limpiar timers de reintento
    if (this.debitRetryTimer) {
      clearInterval(this.debitRetryTimer);
    }
    if (this.creditRetryTimer) {
      clearInterval(this.creditRetryTimer);
    }
  }
}
